import { useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from 'shared/contexts/AuthContext';
import { ColorModeContext } from 'shared/components/layout/ThemeContext';
import { companyApi } from 'shared/services/companyApi';
import { UserRole } from 'shared/types';

/**
 * Hook that automatically loads the appropriate company theme based on context:
 * - If viewing a specific company (via URL param :companyId), load that company's theme
 * - If user is a client role (COMPANY_ADMIN, etc.), load their company's theme
 * - Otherwise, use default theme (no company theme)
 *
 * This hook should be used in the Layout component to ensure theme is loaded
 * based on the current navigation context.
 */
export const useCompanyThemeLoader = () => {
  const { companyId: urlCompanyId } = useParams<{ companyId: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const { setCompanyTheme, setIsLoading } = useContext(ColorModeContext);

  // Track the last loaded company ID to avoid unnecessary reloads
  const lastLoadedCompanyId = useRef<string | null>(null);

  // Determine which company context we're in
  const getActiveCompanyId = useCallback((): string | null => {
    // Priority 1: URL param (admin viewing a specific company)
    if (urlCompanyId) {
      return urlCompanyId;
    }

    // Priority 2: Check if we're in /my-company/* routes (client context)
    if (location.pathname.startsWith('/my-company')) {
      return user?.company?._id || null;
    }

    // Priority 3: Check if we're in /companies/:id/* routes
    const companiesMatch = location.pathname.match(/^\/companies\/([^/]+)/);
    if (companiesMatch) {
      return companiesMatch[1];
    }

    // Priority 4: For company-bound roles (client + contractor), use their company
    const clientRoles = [
      UserRole.COMPANY_ADMIN,
      UserRole.CLIENT_APPROVER,
      UserRole.CLIENT_STAFF,
    ];
    if (user?.role && clientRoles.includes(user.role as UserRole)) {
      return user.company?._id || null;
    }

    // No company context - use default theme
    return null;
  }, [urlCompanyId, location.pathname, user?.company?._id, user?.role]);

  useEffect(() => {
    const activeCompanyId = getActiveCompanyId();

    // If same company, don't reload
    if (activeCompanyId === lastLoadedCompanyId.current) {
      return;
    }

    // Update reference
    lastLoadedCompanyId.current = activeCompanyId;

    // If no company context, clear theme (use defaults)
    if (!activeCompanyId) {
      setCompanyTheme(null);
      return;
    }

    // Load the company's theme
    const loadTheme = async () => {
      setIsLoading(true);
      try {
        const response = await companyApi.getTheme(activeCompanyId);
        const themeData = response.data?.theme;

        if (themeData && (themeData.lightTheme || themeData.darkTheme)) {
          setCompanyTheme(themeData);
        } else {
          // Company exists but has no custom theme
          setCompanyTheme(null);
        }
      } catch (error: unknown) {
        // If theme fetch fails, use defaults
        setCompanyTheme(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, [getActiveCompanyId, setCompanyTheme, setIsLoading]);

  return {
    activeCompanyId: getActiveCompanyId(),
  };
};
